import { type Location } from '@koala/sdk';
import { type ReactNode } from 'react';
import { LOCATION_PILL_COPY } from './constants';
import { useOrgLocations } from './use-org-locations';
import { useSwitchLocation } from './use-switch-location';
import { formatLocationAddress, getLocationName, isLocationSelectable } from './utils';
import { genericEventHandler } from '@/analytics/events';
import { EventNames, GlobalEvents } from '@/analytics/events/constants';
import { styled } from '@/components/content/stitches';
import { MapPinIcon } from '@/components/uielements/icon/map-pin';
import { Modal } from '@/components/uielements/modal';
import { K_ANALYTICS_CTA_LOCATIONS } from '@/constants/events';
import { useSelector } from '@/redux';
import { selectConveyance } from '@/redux/conveyanceMode/reducer';

interface Props {
  trigger: (open: () => void) => ReactNode;
  /** `details` of the analytics event fired on open, so each trigger can be told apart. */
  analyticsDetail?: string;
}

/**
 * "Select Location" pop-up (TEAM-13315): every store of the brand with its address,
 * the current one marked CURRENT. Choosing a store switches to it through
 * {@link useSwitchLocation}; closing without choosing changes nothing.
 */
export function SelectLocationModal({
  trigger,
  analyticsDetail = K_ANALYTICS_CTA_LOCATIONS.LOCATION_PILL,
}: Props) {
  return (
    <Modal.Container
      trigger={(open) =>
        trigger(() => {
          genericEventHandler(GlobalEvents.GENERIC__CTA, {
            name: EventNames.CHANGE_LOCATION,
            details: analyticsDetail,
          });
          open();
        })
      }
    >
      {(close: () => void) => (
        <FullWidth>
          <Modal.Title>{LOCATION_PILL_COPY.modalTitle}</Modal.Title>
          <Modal.Content>
            <LocationList close={close} />
          </Modal.Content>
        </FullWidth>
      )}
    </Modal.Container>
  );
}

function LocationList({ close }: { close: () => void }) {
  const { data: locations, isLoading, isError, refetch } = useOrgLocations();
  const { location: currentId, type: conveyanceType } = useSelector(selectConveyance);
  const webConfig = useSelector((state) => state.app.cmsConfig.webConfig);
  const switchLocation = useSwitchLocation();

  if (isLoading) {
    return <Status role="status">{LOCATION_PILL_COPY.loading}</Status>;
  }

  if (isError || !locations) {
    return (
      <Status role="alert">
        {LOCATION_PILL_COPY.loadError}{' '}
        <RetryButton type="button" onClick={() => void refetch()}>
          {LOCATION_PILL_COPY.retry}
        </RetryButton>
      </Status>
    );
  }

  const choose = (location: Location) => {
    close();
    switchLocation(location);
  };

  return (
    <List data-testid="select-location-list">
      {locations.map((location) => {
        const isCurrent = location.id === currentId;
        const selectable = isCurrent || isLocationSelectable(location, conveyanceType, webConfig);

        return (
          <li key={location.id}>
            <Row
              type="button"
              data-testid={`select-location-row-${location.id}`}
              aria-current={isCurrent ? 'true' : undefined}
              current={isCurrent}
              disabled={!selectable}
              onClick={() => choose(location)}
            >
              <RowPin aria-hidden="true">
                <MapPinIcon title="" width="18" height="18" />
              </RowPin>
              <RowText>
                <RowName>{getLocationName(location)}</RowName>
                <RowAddress>{formatLocationAddress(location)}</RowAddress>
                {!selectable && <RowAddress>{LOCATION_PILL_COPY.unavailable}</RowAddress>}
              </RowText>
              {isCurrent && <Badge>{LOCATION_PILL_COPY.current}</Badge>}
              <Radio aria-hidden="true" checked={isCurrent} />
            </Row>
          </li>
        );
      })}
    </List>
  );
}

const FullWidth = styled('div', {
  width: '100%',
});

const Status = styled('p', {
  color: '$shade600',
  fontSize: '15px',
  margin: 0,
});

const RetryButton = styled('button', {
  background: 'none',
  border: 'none',
  color: '$accentPrimary',
  cursor: 'pointer',
  font: 'inherit',
  padding: 0,
  textDecoration: 'underline',
});

const List = styled('ul', {
  display: 'flex',
  flexDirection: 'column',
  gap: '$3',
  listStyle: 'none',
  margin: 0,
  maxHeight: '60vh',
  overflowY: 'auto',
  padding: 0,
});

const Row = styled('button', {
  alignItems: 'center',
  backgroundColor: '$shade100',
  border: '1px solid $shade300',
  borderRadius: '$md',
  color: '$shade900',
  cursor: 'pointer',
  display: 'flex',
  font: 'inherit',
  gap: '$3',
  padding: '$3 $4',
  textAlign: 'left',
  width: '100%',

  '&:hover:not(:disabled)': {
    borderColor: '$shade600',
  },

  '&:focus-visible': {
    outline: '2px solid $accentPrimary',
    outlineOffset: '2px',
  },

  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.55,
  },

  variants: {
    current: {
      true: {
        // Figma: the current row is tinted with the accent as well as outlined.
        backgroundColor: 'color-mix(in srgb, $colors$accentPrimary 8%, $colors$shade100)',
        border: '2px solid $accentPrimary',
      },
    },
  },
});

const RowPin = styled('span', {
  color: '$accentPrimary',
  display: 'flex',
  flexShrink: 0,
});

const RowText = styled('span', {
  display: 'flex',
  flex: '1 1 auto',
  flexDirection: 'column',
  gap: '$1',
  minWidth: 0,
});

const RowName = styled('span', {
  fontSize: '16px',
  fontWeight: 600,
});

const RowAddress = styled('span', {
  color: '$shade600',
  fontSize: '14px',
});

const Badge = styled('span', {
  backgroundColor: '$accentPrimary',
  borderRadius: '999px',
  color: '$shade100',
  flexShrink: 0,
  fontSize: '10px',
  fontWeight: 800,
  letterSpacing: '$wider',
  padding: '$1 $2',
  textTransform: 'uppercase',
});

const Radio = styled('span', {
  border: '1.5px solid $shade400',
  borderRadius: '50%',
  flexShrink: 0,
  height: '20px',
  width: '20px',

  variants: {
    checked: {
      true: {
        borderColor: '$accentPrimary',
        boxShadow: 'inset 0 0 0 4px $colors$shade100',
        backgroundColor: '$accentPrimary',
      },
    },
  },
});
